import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Icon from '@material-ui/core/Icon';
import classnames from 'classnames';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { onChangeMapFilters } from '../../../ac/onChangeMapFilters';
import { StandardRadioGroup } from '../StandardRadioGroup';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import { LegendRenderer } from '../../LegendRenderer/LegendRenderer';
import { USER_COHORTS_LEGEND, USER_COHORTS_SHARE_LEGEND } from '../../../constants/legends';
import * as V from '../../../constants/variables';
import styles from '../FilterSidebar.module.css';

const H3_ZOOMS = [ 6, 7, 8, 9, 10, 11, 12, 'auto' ];
const PERIOD_TYPES = V.userCohortsPeriodValues;
// DA-15415: два уровня — разрез (возраст / пол / стаж / верификация) и бакет внутри него.
// В отличие от слоя по моделям тарификации, верхний уровень здесь не косметическая
// группировка: разрез уходит query-параметром dimension и определяет, колонки какого
// разреза приедут в тайле. Поэтому при выборе бакета в стор пишутся оба значения —
// dimension и type — одним действием, иначе тайл и раскраска разъедутся.
const UserCohortsFilterComponent = ({
    onChangeMapFilters,
    userCohortsH3Zoom,
    userCohortsDimension,
    userCohortsType,
    userCohortsPeriod,
    userCohortsMode,
}) => {
    const [ isH3ZoomExpanded, setIsH3ZoomExpanded ] = useState(false);
    // Выбор бакета: разрез берём у родителя листа.
    const selectCohort = value => {
        const found = V.findUserCohort(value);

        if (found && found.parent) {
            onChangeMapFilters('userCohortsDimension', found.parent.value);
        }
        onChangeMapFilters('userCohortsType', value);
    };
    const handleCohortChange = ({ target: { value } }) => {
        selectCohort(value);
    };
    // Клик по верхнему уровню: у разреза выбираем его первый бакет, у «Всех поездок» —
    // сам лист. Для «Всех поездок» разрез не меняем: all_cnt приходит в тайле при любом
    // dimension, и возврат к бакетам сохранит последний выбранный разрез.
    const handleDimensionChange = ({ target: { value } }) => {
        const item = V.userCohortsValues.find(({ value: itemValue }) => itemValue === value);

        selectCohort(item && item.children ? item.children[0].value : value);
    };
    const handlePeriodChange = ({ target: { value } }) => {
        onChangeMapFilters('userCohortsPeriod', value);
    };
    // Клик по группе «Месяц» — она сама значением period не бывает, выбираем первый месяц.
    const handlePeriodGroupChange = ({ target: { value } }) => {
        const item = PERIOD_TYPES.find(({ value: itemValue }) => itemValue === value);

        onChangeMapFilters('userCohortsPeriod', item && item.children ? item.children[0].value : value);
    };
    const handleModeChange = ({ target: { value } }) => {
        onChangeMapFilters('userCohortsMode', value);
    };
    const handleH3ZoomChange = ({ target: { value } }) => {
        onChangeMapFilters('userCohortsH3Zoom', value);
    };
    const toggleH3Zoom = () => {
        setIsH3ZoomExpanded(!isH3ZoomExpanded);
    };
    // Раскрыт разрез выбранного бакета; на «Всех поездках» подсвечен верхний лист,
    // но раскрытым остаётся последний выбранный разрез.
    const selected = V.findUserCohort(userCohortsType);
    const selectedDimension = selected && selected.parent ? selected.parent.value : null;
    // Раскрыта группа периодов, если выбран месяц из неё.
    const selectedPeriod = V.findUserCohortPeriod(userCohortsPeriod);
    const selectedPeriodGroup = selectedPeriod && selectedPeriod.parent ? selectedPeriod.parent.value : null;
    const renderRadio = ({ value, label }, isChecked, onChange) => (
        <FormControlLabel
            key={value}
            className={styles.isochroneFilterLabel}
            control={
                <Radio
                    checked={isChecked}
                    onChange={onChange}
                    value={value}
                    className={classnames(
                        styles.isochroneFilterInput, {
                            [styles.checkedIsochroneFilterInput]: isChecked,
                        }
                    )}
                    size="small"
                />
            }
            label={i18n._(label)}
            labelPlacement="end"
        />
    );
    // Для «Всех поездок» доля тождественно равна 100% — тумблер режима прячем,
    // и легенду показываем абсолютную независимо от сохранённого режима.
    const isShareAvailable = userCohortsType !== 'all';
    const isShare = isShareAvailable && userCohortsMode === 'share';

    return (
        <div>
            <div className={styles.legHead}>{t`Разрез`}:</div>
            <StandardRadioGroup
                value={selectedDimension || userCohortsType}
                onChange={handleDimensionChange}
            >
                {
                    V.userCohortsValues.map(item => (item.children
                        ? (
                            // Разрез: клик по нему выбирает первый бакет, бакеты
                            // раскрываются только у выбранного разреза.
                            <React.Fragment key={item.value}>
                                {renderRadio(
                                    item,
                                    selectedDimension === item.value,
                                    handleDimensionChange
                                )}
                                {(selectedDimension === item.value
                                  || (userCohortsType === 'all' && userCohortsDimension === item.value)) && (
                                    <div className={styles.nestedRadioGroup}>
                                        {item.children.map(child => renderRadio(
                                            child,
                                            userCohortsType === child.value,
                                            handleCohortChange
                                        ))}
                                    </div>
                                )}
                            </React.Fragment>
                        )
                        // Лист верхнего уровня («Все поездки») — без бакетов.
                        : renderRadio(item, userCohortsType === item.value, handleCohortChange)))
                }
            </StandardRadioGroup>

            <div className={styles.legHead}>{t`Период`}:</div>
            <StandardRadioGroup
                value={selectedPeriodGroup || userCohortsPeriod}
                onChange={handlePeriodGroupChange}
            >
                {
                    PERIOD_TYPES.map(item => (item.children
                        ? (
                            // Группа «Месяц»: месяцы раскрываются только когда выбран один из них.
                            <React.Fragment key={item.value}>
                                {renderRadio(
                                    item,
                                    selectedPeriodGroup === item.value,
                                    handlePeriodGroupChange
                                )}
                                {selectedPeriodGroup === item.value && (
                                    <div className={styles.nestedRadioGroup}>
                                        {item.children.map(child => renderRadio(
                                            child,
                                            userCohortsPeriod === child.value,
                                            handlePeriodChange
                                        ))}
                                    </div>
                                )}
                            </React.Fragment>
                        )
                        : renderRadio(item, userCohortsPeriod === item.value, handlePeriodChange)))
                }
            </StandardRadioGroup>

            {isShareAvailable && (
                <>
                    <div className={styles.legHead}>{t`Метрика`}:</div>
                    <StandardRadioGroup
                        value={userCohortsMode}
                        onChange={handleModeChange}
                    >
                        {
                            V.aggTracksCohortModeValues.map(({ value, label }) => (
                                <FormControlLabel
                                    key={value}
                                    className={styles.isochroneFilterLabel}
                                    control={
                                        <Radio
                                            checked={userCohortsMode === value}
                                            onChange={handleModeChange}
                                            value={value}
                                            className={classnames(
                                                styles.isochroneFilterInput, {
                                                    [styles.checkedIsochroneFilterInput]: userCohortsMode === value,
                                                }
                                            )}
                                            size="small"
                                        />
                                    }
                                    label={i18n._(label)}
                                    labelPlacement="end"
                                />
                            ))
                        }
                    </StandardRadioGroup>
                </>
            )}

            <div
                className={classnames(styles.legHead, {
                    [styles.expandableLegHead]: true,
                    [styles.expandableLegHeadOpen]: isH3ZoomExpanded,
                })}
                onClick={toggleH3Zoom}
                style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center' }}
            >
                {t`H3 Zoom`}:
                <Icon style={{ marginLeft: 'auto', transition: 'transform 0.25s ease' }}>expand_more</Icon>
            </div>
            {isH3ZoomExpanded && (
                <StandardRadioGroup
                    value={String(userCohortsH3Zoom)}
                    onChange={handleH3ZoomChange}
                >
                    {
                        H3_ZOOMS.map(zoom => (
                            <FormControlLabel
                                key={zoom}
                                className={styles.isochroneFilterLabel}
                                control={
                                    <Radio
                                        checked={String(userCohortsH3Zoom) === String(zoom)}
                                        onChange={handleH3ZoomChange}
                                        value={String(zoom)}
                                        className={classnames(
                                            styles.isochroneFilterInput, {
                                                [styles.checkedIsochroneFilterInput]: String(userCohortsH3Zoom) === String(zoom),
                                            }
                                        )}
                                        size="small"
                                    />
                                }
                                label={zoom}
                            />
                        ))
                    }
                </StandardRadioGroup>
            )}

            <LegendRenderer config={isShare ? USER_COHORTS_SHARE_LEGEND : USER_COHORTS_LEGEND} />
        </div>
    );
};
const mapStateToProps = ({ mapFilters: {
    userCohortsH3Zoom,
    userCohortsDimension,
    userCohortsType,
    userCohortsPeriod,
    userCohortsMode,
} }) => ({
    userCohortsH3Zoom,
    userCohortsDimension,
    userCohortsType,
    userCohortsPeriod,
    userCohortsMode,
});
const ConnectedComponent = connect(mapStateToProps, { onChangeMapFilters })(UserCohortsFilterComponent);

export const UserCohortsFilter = withDisplayedLayersCheck(
    ConnectedComponent,
    t`Треки по когортам пользователей`
);
